/**
 * Na-inspectie Detail Page
 *
 * Form for performing post-inspection quality control of wasserij items.
 * Werkbank-render (fase 2): foto's bovenaan (voor/tijdens-strip + camera),
 * feiten, actie als één-keuze-tegels, opmerkingen aflevering in groepen op
 * meting, één Opslaan vast onderin. Payload ongewijzigd; na-foto's komen uit
 * de media-tabel + deze sessie (geen vinkje).
 * Captures: actie (determines status), opmerkingen voor aflevering, foto's gemaakt.
 * Supports offline saving with sync queue.
 *
 * Actions and their target Status__c:
 * - Nabehandelen -> "In Nabehandeling"
 * - Nogmaals reinigen -> "Klaar voor Reinigen"
 * - Randen plakken -> "Randen Plakken"
 * - Naar reparatie -> "In Reparatie"
 * - Inpakken -> "Ingepakt" + Klaar_voor_aflevering__c = true + Datum_ingepakt__c = TODAY()
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { WbButton, WbChip, WbFactRow, WbFlag, WbInput, WbState, WbTextarea, WbTile, StickyActionBar } from '../components/wb';
import { useHeader } from '../stores';
import { salesforceClient } from '../lib/salesforce/client';
import { isServerWeigering } from '../lib/sync/wasserij-wachtrij';
import {
  NA_INSPECTIE_ACTIES,
  NA_INSPECTIE_AFKEUR_ACTIES,
  NA_INSPECTIE_AFKEUR_REDENEN,
  NA_INSPECTIE_REDEN_TEKST_MAX,
  OPMERKINGEN_AFLEVERING_GROEPEN,
  OPMERKINGEN_AFLEVERING_OPTIES,
  PRIORITY_PRODUCT_CODES,
  type NaInspectieAfkeurReden,
} from '../lib/constants';
import { KleedTijdlijnLijst } from '../components/KleedTijdlijnLijst';
import { CameraCapture } from '../components/CameraCapture';
import { MediaStrip } from '../components/MediaStrip';
import {
  getCachedNaInspectieItems,
  queuePendingNaInspectie,
  removeFromNaInspectieCache,
} from '../lib/db/database';
import { BerichtenInline } from '../components/berichten/BerichtenInline';
import { KlachtBanner } from '../components/KlachtBanner';
import { ReparatieBanner } from '../components/ReparatieBanner';
import type { NaInspectieItem } from '../lib/salesforce';

// Reparatie-statussen die voor de wasserij relevant zijn om te tonen:
// opdracht gaat zeker gebeuren, is bezig, of is net terug voor hercontrole.
const REPARATIE_BANNER_STATUSSEN = ['Klant akkoord', 'Bij reparateur', 'Terug in wasserij'];

// Fasen voor de vergelijkstrip: alles van vóór de kwaliteitscontrole,
// inclusief de geïmporteerde CompanyCam-historie ('import').
const VOOR_TIJDENS_FASEN = ['import', 'voorinspectie', 'reinigen'];

export function NaInspectieDetailPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();

  // Data state
  const [item, setItem] = useState<NaInspectieItem | null>(null);
  const [allItems, setAllItems] = useState<NaInspectieItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [selectedActie, setSelectedActie] = useState<string>('');
  const [selectedReden, setSelectedReden] = useState<NaInspectieAfkeurReden | ''>('');
  const [redenTekst, setRedenTekst] = useState('');
  const [selectedOpmerkingen, setSelectedOpmerkingen] = useState<string[]>([]);
  const [opmerkingenOverig, setOpmerkingenOverig] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  // Server weigerde de save (bv. al ingepakt terwijl een broer nog op Drogen
  // staat: verkeerd label, #144). Tonen, niet in de offline-wachtrij.
  const [weigering, setWeigering] = useState<{ tekst: string; broers: Array<{ itemId: string; itemName: string | null }> } | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [mediaRefresh, setMediaRefresh] = useState(0); // MediaStrip herladen na capture
  // null = nog aan het laden; 0 = geen eigen voor/tijdens-media (vangnet tonen)
  const [voorTijdensAantal, setVoorTijdensAantal] = useState<number | null>(null);
  // Na-foto's: geregistreerd op de server (strip) + deze sessie geschoten
  // (upload kan nog lopen). Samen vervangen ze het handmatige vinkje
  // "Na-inspectie foto's zijn gemaakt" — afgeleid uit de werkelijkheid,
  // niet uit een tweede boekhouding die kan driften.
  const [naFotoAantal, setNaFotoAantal] = useState<number | null>(null);
  const [sessieNaFotos, setSessieNaFotos] = useState(0);
  const naFotosAuto = (naFotoAantal ?? 0) > 0 || sessieNaFotos > 0;

  // Load item data
  useEffect(() => {
    async function loadItem() {
      if (!itemId) return;

      setIsLoading(true);
      setError(null);
      setWeigering(null);

      try {
        // Try API first
        const response = await salesforceClient.getNaInspectieItems();

        if (response.success && response.data?.items) {
          setAllItems(response.data.items);
          const foundItem = response.data.items.find((i) => i.itemId === itemId);

          if (foundItem) {
            setItem(foundItem);
          } else {
            setError('Item niet gevonden');
          }
        } else {
          throw new Error(response.message || 'Kon items niet ophalen');
        }
      } catch (err) {
        console.warn('[NaInspectieDetailPage] API failed, trying cache:', err);

        // Fallback to cache
        try {
          const cached = await getCachedNaInspectieItems();

          if (cached.length > 0) {
            const mappedItems: NaInspectieItem[] = cached.map((c) => ({
              itemId: c.itemId,
              itemName: c.itemName,
              productName: c.productName,
              werkOrderId: c.werkOrderId,
              werkOrderName: c.werkOrderName,
              status: c.status,
              companyCamProjectId: c.companyCamProjectId,
              fotosUrl: c.fotosUrl,
              lengthCm: c.lengthCm,
              widthCm: c.widthCm,
              diameterCm: c.diameterCm,
              surfaceM2: c.surfaceM2,
              vorm: c.vorm,
              voorinspectie: c.voorinspectie,
              voorinspectieOpmerkingen: c.voorinspectieOpmerkingen,
              wasrapport: c.wasrapport,
              isKlacht: c.isKlacht,
              klachtReden: c.klachtReden,
              reparatieNummer: c.reparatieNummer,
              reparatieStatus: c.reparatieStatus,
              reparatieOmschrijving: c.reparatieOmschrijving,
              secondaryItems: c.secondaryItems || [],
            }));

            setAllItems(mappedItems);
            const foundItem = mappedItems.find((i) => i.itemId === itemId);

            if (foundItem) {
              setItem(foundItem);
            } else {
              setError('Item niet gevonden in cache');
            }
          } else {
            setError(err instanceof Error ? err.message : 'Kon item niet laden');
          }
        } catch {
          setError(err instanceof Error ? err.message : 'Kon item niet laden');
        }
      } finally {
        setIsLoading(false);
      }
    }

    loadItem();
  }, [itemId]);

  // Current index in the list (for navigation)
  const currentIndex = useMemo(() => {
    return allItems.findIndex((i) => i.itemId === itemId);
  }, [allItems, itemId]);

  // Next item ID for "Save & Next"
  const nextItemId = useMemo(() => {
    if (currentIndex >= 0 && currentIndex < allItems.length - 1) {
      const nextItem = allItems[currentIndex + 1];
      return nextItem?.itemId ?? null;
    }
    return null;
  }, [allItems, currentIndex]);

  // Sort secondary items: priority items first
  const sortedSecondaryItems = useMemo(() => {
    if (!item?.secondaryItems) return [];
    return [...item.secondaryItems].sort((a, b) => {
      const aIsPriority = PRIORITY_PRODUCT_CODES.includes(a.productCode as typeof PRIORITY_PRODUCT_CODES[number]);
      const bIsPriority = PRIORITY_PRODUCT_CODES.includes(b.productCode as typeof PRIORITY_PRODUCT_CODES[number]);
      if (aIsPriority && !bIsPriority) return -1;
      if (!aIsPriority && bIsPriority) return 1;
      return 0;
    });
  }, [item?.secondaryItems]);

  // Format dimensions
  const formatDimensions = useCallback((item: NaInspectieItem) => {
    if (item.lengthCm && item.widthCm) {
      return `${item.lengthCm} × ${item.widthCm} cm`;
    }
    if (item.diameterCm) {
      return `Ø ${item.diameterCm} cm`;
    }
    if (item.surfaceM2) {
      return `${item.surfaceM2.toFixed(2)} m²`;
    }
    return 'Niet gemeten';
  }, []);

  // Parse voorinspectie multi-picklist
  const voorinspectieValues = useMemo(() => {
    if (!item?.voorinspectie) return [];
    return item.voorinspectie.split(';').map(v => v.trim()).filter(Boolean);
  }, [item?.voorinspectie]);

  // Toggle opmerking selection
  const toggleOpmerking = (value: string) => {
    setSelectedOpmerkingen(prev =>
      prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  };

  // Check if form is valid
  const vraagtReden = NA_INSPECTIE_AFKEUR_ACTIES.has(selectedActie);
  const isFormValid = useMemo(() => {
    if (selectedActie === '') return false;
    if (vraagtReden && selectedReden === '') return false;
    return true;
  }, [selectedActie, vraagtReden, selectedReden]);

  const kiesActie = (actie: string) => {
    setSelectedActie(actie);
    if (!NA_INSPECTIE_AFKEUR_ACTIES.has(actie)) {
      setSelectedReden('');
      setRedenTekst('');
    }
  };

  // Handle save
  const handleSave = async (navigateToNext: boolean = false) => {
    if (!item || !selectedActie) return;

    setIsSaving(true);

    try {
      const saveData = {
        actie: selectedActie,
        opmerkingenAflevering: selectedOpmerkingen.length > 0 ? selectedOpmerkingen.join(';') : undefined,
        opmerkingenAfleveringOverig: opmerkingenOverig.trim() || undefined,
        // Afgeleid uit de media-tabel + deze sessie, geen vinkje
        naFotosGemaakt: naFotosAuto || undefined,
        reden: vraagtReden && selectedReden ? selectedReden : undefined,
        redenTekst: vraagtReden && selectedReden === 'anders' && redenTekst.trim() ? redenTekst.trim() : undefined,
      };

      // Try API first
      const response = await salesforceClient.saveNaInspectie(item.itemId, saveData);

      if (response.success) {
        // Remove from cache
        await removeFromNaInspectieCache(item.itemId);

        // Haptic feedback
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }

        // Navigate
        if (navigateToNext && nextItemId) {
          navigate(`/na-inspectie/${nextItemId}`, { replace: true });
        } else {
          navigate('/na-inspectie', { replace: true });
        }
      } else {
        throw new Error(response.message || 'Opslaan mislukt');
      }
    } catch (err) {
      if (isServerWeigering(err)) {
        const body = err.originalError as { broers?: Array<{ itemId: string; itemName: string | null }> } | undefined;
        setWeigering({ tekst: err.message, broers: body?.broers ?? [] });
        if (err.errorCode === 'AL_INGEPAKT_BROER_OPEN') await removeFromNaInspectieCache(item.itemId);
        if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 100]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      console.warn('[NaInspectieDetailPage] API save failed, queueing offline:', err);

      // Queue for offline sync
      await queuePendingNaInspectie({
        itemId: item.itemId,
        itemName: item.itemName,
        actie: selectedActie,
        opmerkingenAflevering: selectedOpmerkingen.length > 0 ? selectedOpmerkingen.join(';') : undefined,
        opmerkingenAfleveringOverig: opmerkingenOverig.trim() || undefined,
        naFotosGemaakt: naFotosAuto || undefined,
        reden: vraagtReden && selectedReden ? selectedReden : undefined,
        redenTekst: vraagtReden && selectedReden === 'anders' && redenTekst.trim() ? redenTekst.trim() : undefined,
      });

      // Remove from cache (optimistic)
      await removeFromNaInspectieCache(item.itemId);

      // Different haptic pattern for offline save
      if (navigator.vibrate) {
        navigator.vibrate([50, 30, 50]);
      }

      // Navigate anyway
      if (navigateToNext && nextItemId) {
        navigate(`/na-inspectie/${nextItemId}`, { replace: true });
      } else {
        navigate('/na-inspectie', { replace: true });
      }
    } finally {
      setIsSaving(false);
    }
  };

  useHeader(item ? item.itemName : 'Na-inspectie', item && allItems.length > 1 ? `${currentIndex + 1} / ${allItems.length}` : null);

  if (isLoading) {
    return <WbState variant="laden" titel="Item laden…" className="text-center min-h-[60vh] flex items-center justify-center" />;
  }

  if (error || !item) {
    return (
      <div className="space-y-3">
        <Link to="/na-inspectie" className="wb-focus inline-flex items-center min-h-tap font-semibold underline underline-offset-4 text-wb-inkt">
          ‹ Terug naar lijst
        </Link>
        <WbFlag kind="geld" title="Kon item niet laden">{error || 'Item niet gevonden'}</WbFlag>
      </div>
    );
  }

  const pakket = sortedSecondaryItems.find((s) => s.category === 'Package')?.productName;
  const extras = sortedSecondaryItems.filter((s) => s.category !== 'Package');
  const naAantal = (naFotoAantal ?? 0) + sessieNaFotos;

  return (
    <div className="space-y-5 pb-[140px]">
      {weigering && (
        <WbFlag kind="geld" title={weigering.broers.length > 0 ? 'Niet ingepakt: verkeerd label?' : 'Niet opgeslagen'}>
          <p>{weigering.tekst}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {weigering.broers.map((b) => (
              <WbButton key={b.itemId} variant="secondary" onClick={() => { setWeigering(null); navigate(`/na-inspectie/${b.itemId}`, { replace: true }); }}>
                Open {b.itemName ?? 'kleed'}
              </WbButton>
            ))}
          </div>
        </WbFlag>
      )}
      {/* Terug + WI groot + positie */}
      <div className="flex items-center justify-between gap-2">
        <Link to="/na-inspectie" className="wb-focus inline-flex items-center gap-1 min-h-tap font-mono text-xl font-semibold text-wb-inkt">
          <span aria-hidden>‹</span> {item.itemName}
        </Link>
        <WbChip kind={item.status === 'In Reparatie' ? 'actie' : item.status === 'Drogen' ? 'neutraal' : 'wacht'}>{item.status}</WbChip>
      </div>

      {item.isKlacht && <KlachtBanner reden={item.klachtReden} />}
      {item.reparatieStatus && REPARATIE_BANNER_STATUSSEN.includes(item.reparatieStatus) && (
        <ReparatieBanner nummer={item.reparatieNummer} status={item.reparatieStatus} omschrijving={item.reparatieOmschrijving} />
      )}

      {/* Foto's bovenaan: voor/tijdens als vergelijking, camera voor de na-foto's */}
      <Sectie titel="Voor en tijdens">
        <MediaStrip wiSfId={item.itemId} refreshKey={mediaRefresh} toonFasen={VOOR_TIJDENS_FASEN} onGeladen={setVoorTijdensAantal} />
        {voorTijdensAantal === 0 && item.fotosUrl && (
          <a href={item.fotosUrl} target="_blank" rel="noopener noreferrer" className="wb-focus inline-flex min-h-tap items-center font-semibold underline underline-offset-4 text-wb-inkt">
            Bekijk oude voorfoto's
          </a>
        )}
        {voorTijdensAantal === 0 && !item.fotosUrl && <p className="text-sm text-wb-inkt2">Geen voorfoto's gevonden.</p>}
      </Sectie>

      <Sectie titel="Na-foto">
        <WbButton variant="secondary" onClick={() => setShowCamera(true)}>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Na-foto's maken
        </WbButton>
        {/* Social-Reels zetten het kleed recht van boven; dat lukt alleen met het hele kleed in beeld (plan 2026-09-01). */}
        <p className="text-sm text-wb-inkt2">Eén foto van iets verder weg met het hele kleed in beeld, franjes erbij. Van dichtbij mag daarna.</p>
        <MediaStrip wiSfId={item.itemId} refreshKey={mediaRefresh} toonFasen={['na-inspectie']} onGeladen={setNaFotoAantal} />
      </Sectie>

      {/* Feiten + fototellers: nul na-foto's is iets om te lezen (oranje), geen geld */}
      <WbFactRow
        facts={[
          { label: 'Maat', value: formatDimensions(item) },
          { label: 'Pakket', value: pakket ?? item.productName, tekst: true },
          { label: 'Order', value: item.werkOrderName },
        ]}
      />
      {extras.length > 0 && (
        <div className="flex flex-wrap gap-1 -mt-3">
          {extras.map((e) => (
            <WbChip key={e.itemId} kind={e.productCode === 'WAS-PRIM-VLOERKLEED-SPOED' ? 'actie' : e.productCode === 'WAS-PACK-VLOERKLEED-PREMIUM' ? 'klaar' : 'neutraal'}>
              {e.productName}
            </WbChip>
          ))}
        </div>
      )}
      <WbFactRow
        facts={[
          { label: 'Voor/tijdens', value: voorTijdensAantal ?? '…' },
          { label: 'Na-foto\'s', value: naFotoAantal === null ? '…' : naAantal },
        ]}
      />
      {naFotoAantal !== null && naAantal === 0 && (
        <WbFlag kind="opmerking" title="Nog geen na-foto's">Maak eerst de na-foto's; de klant krijgt ze in het rapport.</WbFlag>
      )}

      {/* Berichten */}
      <BerichtenInline
        scope="wi"
        werkOrderId={item.werkOrderId}
        werkOrderName={item.werkOrderName}
        wasserijItemId={item.itemId}
        wasserijItemName={item.itemName}
      />

      {/* Voorinspectie en tijdlijn: lezen. Wasrapport-tekst alleen als er geen tijdlijn is. */}
      {(voorinspectieValues.length > 0 || item.voorinspectieOpmerkingen || item.wasrapport || (item.tijdlijn?.length ?? 0) > 0) && (
        <Sectie titel="Voorinspectie en wasrapport">
          {voorinspectieValues.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {voorinspectieValues.map((v) => <WbChip key={v} kind="neutraal">{v}</WbChip>)}
            </div>
          )}
          {item.voorinspectieOpmerkingen && <WbFlag kind="info" title="Opmerkingen voorinspectie">{item.voorinspectieOpmerkingen}</WbFlag>}
          {item.tijdlijn && item.tijdlijn.length > 0
            ? <KleedTijdlijnLijst stappen={item.tijdlijn} />
            : item.wasrapport && <WbFlag kind="info" title="Wasrapport">{item.wasrapport}</WbFlag>}
        </Sectie>
      )}

      {/* Actie: één keuze, vijf tegels */}
      <Sectie titel="Actie">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
          {NA_INSPECTIE_ACTIES.map((actie) => (
            <WbTile
              key={actie.value}
              rol="radio"
              selected={selectedActie === actie.value}
              onToggle={() => kiesActie(actie.value)}
              wide={actie.value === 'inpakken'}
            >
              {actie.label}
            </WbTile>
          ))}
        </div>
      </Sectie>

      {/* Reden: alleen bij afkeuren (nabehandelen, nogmaals reinigen) */}
      {vraagtReden && (
        <Sectie titel="Reden">
          <div className="grid grid-cols-3 gap-1.5">
            {NA_INSPECTIE_AFKEUR_REDENEN.map((reden) => (
              <WbTile
                key={reden.value}
                rol="radio"
                selected={selectedReden === reden.value}
                onToggle={() => setSelectedReden(reden.value)}
              >
                {reden.label}
              </WbTile>
            ))}
          </div>
          {selectedReden === 'anders' && (
            <WbInput
              label="Toelichting"
              value={redenTekst}
              maxLength={NA_INSPECTIE_REDEN_TEKST_MAX}
              onChange={(e) => setRedenTekst(e.target.value.slice(0, NA_INSPECTIE_REDEN_TEKST_MAX))}
              placeholder="Wat is er mis?"
            />
          )}
        </Sectie>
      )}

      {/* Opmerkingen voor aflevering: alleen bij inpakken, in groepen op meting */}
      {selectedActie === 'inpakken' && (
        <Sectie titel="Opmerkingen voor de klant">
          {OPMERKINGEN_AFLEVERING_GROEPEN.map((groep) => (
            <div key={groep.key} className="space-y-1.5">
              <p className="text-[11px] font-bold uppercase tracking-wide text-wb-inkt3 pt-1">{groep.label}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                {OPMERKINGEN_AFLEVERING_OPTIES.filter((o) => o.groep === groep.key).map((optie) => (
                  <WbTile key={optie.value} selected={selectedOpmerkingen.includes(optie.value)} onToggle={() => toggleOpmerking(optie.value)}>
                    {optie.label}
                  </WbTile>
                ))}
              </div>
            </div>
          ))}
          {selectedOpmerkingen.length > 0 && <p className="font-mono text-sm text-wb-inkt2">{selectedOpmerkingen.length} geselecteerd</p>}
          <WbTextarea
            label="Overige opmerkingen (komt bij de klant)"
            value={opmerkingenOverig}
            onChange={(e) => setOpmerkingenOverig(e.target.value)}
            placeholder="Eventuele extra opmerkingen voor de klant…"
          />
        </Sectie>
      )}

      {/* Eén knop: Opslaan gaat terug naar de lijst (zelfde besluit als Voorinspectie). */}
      <StickyActionBar>
        <WbButton size="lg" onClick={() => handleSave(false)} disabled={!isFormValid} isLoading={isSaving}>
          {isFormValid ? 'Opslaan' : selectedActie === '' ? 'Kies een actie' : 'Kies een reden'}
        </WbButton>
      </StickyActionBar>

      {showCamera && (
        <CameraCapture
          fase="na-inspectie"
          doel={{ wiSfId: item.itemId }}
          subtitle={item.itemName}
          captureStatus={item.status}
          onClose={(aantal) => {
            setShowCamera(false);
            if (aantal > 0) {
              setSessieNaFotos((n) => n + aantal);
              setMediaRefresh((k) => k + 1);
            }
          }}
        />
      )}
    </div>
  );
}

function Sectie({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="font-wb-heading font-bold text-xs uppercase tracking-wide text-wb-inkt2">{titel}</h2>
      {children}
    </section>
  );
}
